(() => {
  const productCache = new Map();
  const MAX_VISIBLE_SWATCHES = 6;
  const deferredInitializers = new WeakMap();
  const viewportObserver = 'IntersectionObserver' in window
    ? new IntersectionObserver(
        entries => {
          entries.forEach(entry => {
            if (!entry.isIntersecting) return;
            viewportObserver.unobserve(
              entry.target
            );
            const initialize =
              deferredInitializers.get(
                entry.target
              );
            deferredInitializers.delete(
              entry.target
            );
            initialize?.();
          });
        },
        {
          rootMargin: '400px 0px'
        }
      )
    : null;

  function initializeNearViewport(
    element,
    initialize
  ) {
    if (!viewportObserver) {
      initialize();
      return;
    }

    deferredInitializers.set(
      element,
      initialize
    );
    viewportObserver.observe(element);
  }

  function resizeShopifyImage(url, width) {
    if (!url) return '';

    const normalizedUrl = url.startsWith('//')
      ? `${window.location.protocol}${url}`
      : url;

    const separator = normalizedUrl.includes('?')
      ? '&'
      : '?';

    return `${normalizedUrl}${separator}width=${width}`;
  }

  function getVariantImage(variant) {
    return (
      variant?.featured_media?.preview_image?.src ||
      variant?.featured_image?.src ||
      ''
    );
  }

  function getCardRoot(element) {
    return (
      element.closest('.card-wrapper') ||
      element.closest('.card')
    );
  }

  function updateCardImages(
    cardRoot,
    container,
    item
  ) {
    if (!cardRoot || !item.image) return;

    const images = Array.from(
      cardRoot.querySelectorAll(
        '.card__media img'
      )
    );

    if (images.length === 0) return;

    const widths = [
      165,
      360,
      533,
      720,
      940
    ];

    const src = resizeShopifyImage(
      item.image,
      533
    );

    const srcset = widths
      .map(
        width =>
          `${resizeShopifyImage(
            item.image,
            width
          )} ${width}w`
      )
      .join(', ');

    /*
     * 同时更新主图和悬停图，
     * 防止鼠标移开后才切图。
     */
    images.forEach(image => {
      image.src = src;
      image.srcset = srcset;
      image.alt = item.value;
    });

    container
      .querySelectorAll(
        '.card-image-swatch'
      )
      .forEach(button => {
        const selected =
          button.dataset.value ===
          item.value;

        button.classList.toggle(
          'is-selected',
          selected
        );

        button.setAttribute(
          'aria-pressed',
          selected ? 'true' : 'false'
        );
      });
  }

  function addSwatch(
    container,
    item,
    index,
    onClick
  ) {
    const button =
      document.createElement('button');

    button.type = 'button';
    button.className =
      'card-image-swatch';

    button.dataset.value = item.value;
    button.dataset.variantId =
      item.variantId;

    button.title = item.value;

    button.setAttribute(
      'aria-label',
      `View ${item.value}`
    );

    button.setAttribute(
      'aria-pressed',
      index === 0 ? 'true' : 'false'
    );

    if (index === 0) {
      button.classList.add(
        'is-selected'
      );
    }

    if (item.image) {
      button.style.backgroundImage =
        `url("${resizeShopifyImage(
          item.image,
          100
        )}")`;
    }

    button.addEventListener(
      'click',
      event => {
        event.preventDefault();
        event.stopPropagation();

        onClick(item);
      }
    );

    container.appendChild(button);
  }
  function addMoreSwatch(
  container,
  remaining,
  productUrl
) {
  const link =
    document.createElement('a');

  link.className =
    'card-image-swatch card-image-swatch-more';

  link.href = productUrl || '#';
  link.textContent = `+${remaining}`;
  link.title = `${remaining} more styles`;

  link.setAttribute(
    'aria-label',
    `View ${remaining} more styles`
  );

  link.addEventListener(
    'click',
    event => {
      event.stopPropagation();
    }
  );

  container.appendChild(link);
}

function renderSwatches(
  container,
  items,
  productUrl,
  onClick
) {
  items
    .slice(0, MAX_VISIBLE_SWATCHES)
    .forEach((item, index) => {
      addSwatch(
        container,
        item,
        index,
        onClick
      );
    });

  const remaining =
    items.length -
    MAX_VISIBLE_SWATCHES;

  if (remaining > 0) {
    addMoreSwatch(
      container,
      remaining,
      productUrl
    );
  }
}

  function uniqueVariantItems(
    variants,
    optionIndex
  ) {
    const items = new Map();

    variants.forEach(variant => {
      const value =
        variant.options?.[optionIndex] ??
        variant[
          `option${optionIndex + 1}`
        ];

      if (!value) return;

      const image =
        getVariantImage(variant);

      if (!items.has(value)) {
        items.set(value, {
          value,
          image,
          variantId: variant.id
        });
      } else if (
        !items.get(value).image &&
        image
      ) {
        items.set(value, {
          value,
          image,
          variantId: variant.id
        });
      }
    });

    return Array.from(items.values());
  }

  /*
   * 处理首页已经存在的 Shrine 色块。
   */
  function buildExistingSwatches(
    cardVariantSelects
  ) {
    if (
      cardVariantSelects.dataset
        .imageSwatchesReady === 'true'
    ) {
      return;
    }

    const container =
      cardVariantSelects.querySelector(
        '.card__swatches-container'
      );

    const variantJson =
      cardVariantSelects.querySelector(
        'script[type="application/json"]'
      );

    if (!container || !variantJson) {
      return;
    }

    let variants;

    try {
      variants = JSON.parse(
        variantJson.textContent
      );
    } catch (error) {
      console.warn(
        'Product card variants could not be read.',
        error
      );

      return;
    }

    const optionGroups = Array.from(
      cardVariantSelects.querySelectorAll(
        '.product-form__input'
      )
    );

    const colorGroup =
      container.closest(
        '.product-form__input'
      );

    let colorOptionIndex =
      optionGroups.indexOf(colorGroup);

    if (colorOptionIndex < 0) {
      colorOptionIndex = 0;
    }

    const items = uniqueVariantItems(
      variants,
      colorOptionIndex
    );

    if (items.length === 0) return;

    container.innerHTML = '';

    container.classList.add(
      'card-image-swatches'
    );

    const cardRoot = getCardRoot(
      cardVariantSelects
    );

   renderSwatches(
  container,
  items,
  cardVariantSelects.dataset.url,
  selected => {
    updateCardImages(
      cardRoot,
      container,
      selected
    );
  }
);

    cardVariantSelects.dataset
      .imageSwatchesReady = 'true';
  }

  /*
   * 自动识别颜色选项。
   */
  function getColorOptionIndex(product) {
    return (
      product.options || []
    ).findIndex(option => {
      const name =
        typeof option === 'string'
          ? option
          : option?.name || '';

      const normalized = name
        .toLowerCase()
        .replace(/[:：]/g, '')
        .trim();

      return (
        normalized.includes('color') ||
        normalized.includes('colour') ||
        normalized.includes('颜色') ||
        normalized.includes('顏色')
      );
    });
  }

  /*
   * 获取集合页商品的变体数据。
   */
  function getProduct(productUrl) {
    if (!productCache.has(productUrl)) {
      const url = new URL(
        productUrl,
        window.location.origin
      );

      url.pathname =
        `${url.pathname.replace(
          /\/$/,
          ''
        )}.js`;

      productCache.set(
        productUrl,
        fetch(url.toString(), {
          headers: {
            Accept: 'application/json'
          }
        })
          .then(response => {
            if (!response.ok) {
              throw new Error(
                `HTTP ${response.status}`
              );
            }

            return response.json();
          })
          .catch(error => {
            productCache.delete(
              productUrl
            );

            throw error;
          })
      );
    }

    return productCache.get(productUrl);
  }

  /*
   * 将集合页的 Choose options
   * 替换为服饰图片色块。
   */
  async function replaceChooseOptions(
    button
  ) {
    if (
      button.dataset
        .imageSwatchesLoading ===
      'true'
    ) {
      return;
    }

    button.dataset
      .imageSwatchesLoading = 'true';

    try {
      const product = await getProduct(
        button.dataset.productUrl
      );

      const colorOptionIndex =
        getColorOptionIndex(product);

      /*
       * 没有颜色选项时，
       * 保留原 Choose options。
       */
      if (colorOptionIndex < 0) return;

      const items = uniqueVariantItems(
        product.variants || [],
        colorOptionIndex
      ).filter(item => item.image);

      /*
       * 没有变体图片时，
       * 保留原 Choose options。
       */
      if (items.length === 0) return;

      const quickAdd =
        button.closest('.quick-add');

      const cardRoot =
        getCardRoot(button);

      if (!quickAdd || !cardRoot) {
        return;
      }

      const container =
        document.createElement('div');

      container.className =
        'card__swatches-container card-image-swatches collection-card-image-swatches';

      renderSwatches(
  container,
  items,
  button.dataset.productUrl,
  selected => {
    updateCardImages(
      cardRoot,
      container,
      selected
    );
  }
);

const cardContent =
  quickAdd.parentElement;

const cardInformation =
  Array.from(
    cardContent.children
  ).find(element =>
    element.classList.contains(
      'card__information'
    )
  );

if (!cardInformation) return;

/*
 * 把色块放进标题和价格区域末尾。
 */
cardInformation.appendChild(
  container
);

/*
 * 删除原来的 Choose options。
 */
quickAdd.remove();
    } catch (error) {
      button.dataset
        .imageSwatchesLoading =
        'false';

      console.warn(
        'Collection card swatches could not be loaded.',
        error
      );
    }
  }
async function buildHomepageFallbackSwatches(
  cardWrapper
) {
  if (
    cardWrapper.dataset
      .imageSwatchesLoading === 'true' ||
    cardWrapper.dataset
      .imageSwatchesReady === 'true' ||
    cardWrapper.querySelector(
      'card-variant-selects'
    ) ||
    cardWrapper.querySelector(
      '.card-image-swatches'
    )
  ) {
    return;
  }

  const card = cardWrapper.querySelector(
    ':scope > .card'
  );

  const cardInformation =
    card?.querySelector(
      ':scope > .card__content > .card__information'
    );

  const productLink =
    cardInformation?.querySelector(
      '.card__heading a[href*="/products/"]'
    );

  const productUrl =
    productLink?.getAttribute('href');

  if (
    !card ||
    !cardInformation ||
    !productUrl
  ) {
    return;
  }

  cardWrapper.dataset
    .imageSwatchesLoading = 'true';

  try {
    const product =
      await getProduct(productUrl);

    const colorOptionIndex =
      getColorOptionIndex(product);

    if (colorOptionIndex < 0) return;

    const items = uniqueVariantItems(
      product.variants || [],
      colorOptionIndex
    ).filter(item => item.image);

    if (items.length === 0) return;

    const container =
      document.createElement('div');

    container.className =
      'card__swatches-container card-image-swatches collection-card-image-swatches homepage-fallback-image-swatches';

    renderSwatches(
      container,
      items,
      productUrl,
      selected => {
        updateCardImages(
          cardWrapper,
          container,
          selected
        );
      }
    );

    cardInformation.appendChild(
      container
    );

    cardWrapper.dataset
      .imageSwatchesReady = 'true';
  } catch (error) {
    cardWrapper.dataset
      .imageSwatchesLoading = 'false';

    console.warn(
      'Homepage card swatches could not be loaded.',
      error
    );
  }
}
  function initializeImageSwatches(
    root = document
  ) {
    /*
     * 首页产品卡。
     */
    const existing = [];

    if (
      root.matches?.(
        'card-variant-selects'
      )
    ) {
      existing.push(root);
    }

    root
      .querySelectorAll?.(
        'card-variant-selects'
      )
      .forEach(element => {
        existing.push(element);
      });

    existing.forEach(element => {
      initializeNearViewport(
        element,
        () => buildExistingSwatches(element)
      );
    });
const homepageCardSelector =
  'section[id*="__featured_collection_"] .card-wrapper.product-card-wrapper';

const homepageCards = [];

if (
  root.matches?.(
    homepageCardSelector
  )
) {
  homepageCards.push(root);
}

root
  .querySelectorAll?.(
    homepageCardSelector
  )
  .forEach(card => {
    homepageCards.push(card);
  });

homepageCards.forEach(card => {
  initializeNearViewport(
    card,
    () => buildHomepageFallbackSwatches(card)
  );
});
    /*
     * 集合页主产品列表。
     * 不处理产品详情页和购物车。
     */
    const collectionButtonSelector =
      '#ProductGridContainer .quick-add__submit[data-product-url]';

    const quickAddButtons = [];

    if (
      root.matches?.(
        collectionButtonSelector
      )
    ) {
      quickAddButtons.push(root);
    }

    root
      .querySelectorAll?.(
        collectionButtonSelector
      )
      .forEach(button => {
        quickAddButtons.push(button);
      });

    quickAddButtons.forEach(button => {
      initializeNearViewport(
        button,
        () => replaceChooseOptions(button)
      );
    });
  }

  function startImageSwatches() {
    initializeImageSwatches();

    /*
     * 支持主题编辑器重新加载。
     */
    document.addEventListener(
      'shopify:section:load',
      event => {
        initializeImageSwatches(
          event.target
        );
      }
    );

    /*
     * 支持集合筛选和排序后
     * 重新生成的产品卡。
     */
    const observer =
      new MutationObserver(
        mutations => {
          mutations.forEach(
            mutation => {
              mutation.addedNodes.forEach(
                node => {
                  if (
                    node.nodeType === 1
                  ) {
                    initializeImageSwatches(
                      node
                    );
                  }
                }
              );
            }
          );
        }
      );

    observer.observe(
      document.body,
      {
        childList: true,
        subtree: true
      }
    );
  }

  if (
    document.readyState === 'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      startImageSwatches
    );
  } else {
    startImageSwatches();
  }
})();
